
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Stack,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyIcon from '@mui/icons-material/Key';
import ChangePasswordDialog from './ChangePasswordDialog';

export default function UserMenu({ user, onLogout, isGlobalAdmin, sectionTitle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPw, setOpenPw] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Get role label from user context
  const getRoleLabel = () => {
    const role = user?.context?.highest_role;
    switch (role) {
      case 'global_admin':
        return 'Global Admin';
      default:
        return null;
    }
  };

  const roleLabel = getRoleLabel();
  const showRoleBadge = roleLabel !== null;

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const handleChangePassword = () => {
    handleMenuClose();
    setOpenPw(true);
  };

  const initials = `${user.user_first_name?.[0] ?? ''}${user.user_last_name?.[0] ?? ''}`.toUpperCase();
  const title = sectionTitle || 'PM System PRO';

  return (
    <>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(14px)',
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          zIndex: theme => theme.zIndex.drawer + 1,
          left: { xs: 0, md: '280px' },
          width: { xs: '100%', md: 'calc(100% - 280px)' }
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 88 }, gap: 2, px: { xs: 2, md: 3 } }}>
          {/* Mobile: Space for hamburger button */}
          {isMobile && <Box sx={{ width: 48 }} />}

          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1, display: { xs: 'none', sm: 'block' }, fontSize: { xs: '0.65rem', md: '0.75rem' }, lineHeight: 1.2 }}
              >
                PM System PRO
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {title}
              </Typography>
            </Box>
            {showRoleBadge && (
              <Box
                component="span"
                sx={{
                  px: { xs: 1, md: 1.5 },
                  py: 0.5,
                  borderRadius: 12,
                  fontSize: { xs: 10, md: 12 },
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  bgcolor: 'rgba(47, 74, 225, 0.12)',
                  color: 'primary.main',
                  pointerEvents: 'none',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {roleLabel}
              </Box>
            )}
          </Stack>

          <Stack direction="row" spacing={{ xs: 0.5, md: 2 }} alignItems="center">
            {!isMobile && (
              <Avatar sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 600 }}>
                {initials || user.user_first_name?.[0] || '?'}
              </Avatar>
            )}

            {isMobile ? (
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'transparent', fontSize: '0.9rem' }}>
                  {initials || user.user_first_name?.[0] || '?'}
                </Avatar>
              </IconButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleMenuOpen}
                endIcon={<ArrowDropDownIcon />}
                sx={{ borderRadius: 999, fontWeight: 600 }}
              >
                {user.user_first_name} {user.user_last_name}
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleChangePassword}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <KeyIcon fontSize="small" />
            <Typography variant="body2">Change Password</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
            <Typography variant="body2">Log Out</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <ChangePasswordDialog
        open={openPw}
        onClose={() => setOpenPw(false)}
        userId={user.user_id}
        onSuccess={() => setSnackbar({ open: true, message: 'Password changed successfully' })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
